import React, { FC } from 'react';
import styled from 'styled-components';
import { colors } from 'styles/colors';
import moment from 'moment';
import { isTrialBooking } from 'logic/helpers/tutorHelper';
import { Text } from 'components/text/Text';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { useBreakpoints } from 'logic/hooks/use-breakpoints';
import {
  Booking,
  SeekerCreditEventType,
  ElitetutorCreditEventType,
  TutorCreditEventFlags,
  SeekerCreditEventFlags,
  TutorCreditEvent,
} from 'logic/api-models/api-models';
import { useSelector } from 'react-redux';
import { getSubjectsByNumber } from 'logic/store/subject/subject.selectors';
import { generatePriceFromChangeProp } from 'src/pages-all/user/user-profile/earnings/earnings.logic';

const BookingHistoryItemCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 155px;
  border-radius: 16px;
  box-shadow: 1px 1px 10px -6px ${colors.black};
  background-color: ${colors.white};
  padding: 20px;
  margin: 10px 5px;
`;

const BookingHistoryItemWrapper = styled.div<{ maxTablet?: boolean; maxMobileLarge?: boolean }>`
  flex-wrap: wrap;
  display: ${({ maxTablet }) => (maxTablet ? 'grid' : 'flex')};
  flex-direction: ${({ maxTablet }) => (maxTablet ? 'column' : 'row')};
  grid-template-columns: repeat(auto-fit, minmax(100px, 130px));
  align-items: center;
  justify-content: space-between;
  place-items: center;
  text-align: center;
  padding: 5px;
  column-gap: 10px;
  ${({ maxTablet }) => (maxTablet ? 'column' : 'row')};
`;

const EmojiSpan = styled.span`
  font-size: 20px;
  vertical-align: middle;
`;

const ShortText = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50px;
  grid-column: span 5;
`;

const Hint = styled.div`
  margin: 0 auto;
`;

const HistoryItemDefaultPart: FC<{ label: string; value: string }> = ({ label, value }) => {
  return (
    <div>
      <Text size={16} color={'blueGray'} fontWeight={600} style={{ display: 'block' }}>
        {label}
      </Text>
      <Text size={15} color={'blueGray'} style={{ display: 'block' }}>
        {value}
      </Text>
    </div>
  );
};

type props = {
  type: SeekerCreditEventType;
  credits: number;
  booking?: Booking;
  flags?: SeekerCreditEventFlags;
};

export const BookingHistoryItem: FC<props> = ({ booking, type, credits, flags }) => {
  const subjects = useSelector(getSubjectsByNumber) || {};
  const _isTrialBooking = isTrialBooking(booking);
  const tutor = booking?.tutor;
  const { maxTablet } = useBreakpoints();

  const isDeletedTutor = () => !tutor?.firstName;
  const yourTutor = isDeletedTutor()
    ? translate(translationKeys.offerRequestMessages.anonymousUser)
    : tutor?.firstName + ' ' + tutor?.lastName;

  return (
    <BookingHistoryItemCard>
      {type === SeekerCreditEventType.no_show_refund && (
        <Hint>
          <Text size={17} color={'blueGray'} fontWeight={600} style={{ display: 'block' }}>
            {flags?.seekerNoShow && flags?.tutorNoShow
              ? translate(translationKeys.billing.bookingHistory.bothNoShowRefund)
              : translate(translationKeys.billing.bookingHistory.tutorNoShowRefund)}
          </Text>
        </Hint>
      )}
      <BookingHistoryItemWrapper maxTablet={maxTablet}>
        <HistoryItemDefaultPart
          label={translate(translationKeys.billing.bookingHistory.yourTutor)}
          value={yourTutor}
        />
        <HistoryItemDefaultPart
          label={translate(translationKeys.billing.bookingHistory.time)}
          value={moment(booking?.start).format('HH:mm')}
        />
        <HistoryItemDefaultPart
          label={translate(translationKeys.billing.bookingHistory.date)}
          value={moment(booking?.start).format('DD.MM.YYYY')}
        />
        <HistoryItemDefaultPart
          label={translate(translationKeys.billing.bookingHistory.lessonType)}
          value={_isTrialBooking ? 'Probestunde' : 'Regulär'}
        />
        <HistoryItemDefaultPart
          label={translate(translationKeys.billing.bookingHistory.subject)}
          value={subjects[booking?.details?.subject || 0]?.subjectName || 'UNKNOWN'}
        />
        <div style={{ gridRow: 'span 2 / auto' }}>
          <Text size={20} color={'blueGray'} fontWeight={600} style={{ display: 'block' }}>
            {credits > 0 ? `+${credits}\xa0h` : `${credits}\xa0h`}
          </Text>
        </div>
      </BookingHistoryItemWrapper>
    </BookingHistoryItemCard>
  );
};

export const BookingHistoryItemWithText: FC<{
  credits: number;
  type: SeekerCreditEventType;
  userNote?: string;
}> = ({ credits, type, userNote }) => {
  const { maxMobileLarge, maxTablet } = useBreakpoints();

  let emoji = '';
  let descriptionText = '';

  switch (type) {
    case SeekerCreditEventType.booking_canceled:
      emoji = '➕';
      descriptionText = translate(
        translationKeys.billing.bookingHistory.receivedCreditsFromCanceledBooking
      );
      break;
    case SeekerCreditEventType.credit_expired:
      emoji = '😢';
      descriptionText = translate(translationKeys.billing.bookingHistory.expiredCredits);
      break;
    case SeekerCreditEventType.credit_issued:
      descriptionText = translate(translationKeys.billing.bookingHistory.receivedCredits);
      emoji = '🥳';
      break;
    case SeekerCreditEventType.credit_restored:
    case SeekerCreditEventType.correction_add:
    case SeekerCreditEventType.correction_subtract:
      emoji = '⚙️';
      descriptionText =
        translate(translationKeys.billing.bookingHistory.correction) +
        (userNote
          ? ' ' +
            translate(translationKeys.billing.bookingHistory.correctionNoteByAdmin, {
              note: userNote,
            })
          : '');
      break;
    case SeekerCreditEventType.free_booking_grant:
      descriptionText = translate(translationKeys.billing.bookingHistory.freeBookingGranted);
      emoji = '⚙️';
      break;
    case SeekerCreditEventType.free_booking_revoke:
      descriptionText = translate(translationKeys.billing.bookingHistory.freeBookingRevoked);
      emoji = '⚙️';
      break;
    default:
      break;
  }

  return (
    <BookingHistoryItemCard>
      <BookingHistoryItemWrapper maxTablet={maxTablet}>
        {!maxMobileLarge && (
          <ShortText>
            <EmojiSpan role={'img'} aria-label={'party-smiley'}>
              {emoji}
            </EmojiSpan>
          </ShortText>
        )}
        <div style={{ gridColumn: 'span 5 / auto', textAlign: 'left', flex: '1' }}>
          <Text
            size={17}
            color={'blueGray'}
            fontWeight={600}
            style={{ display: 'block', textAlign: maxTablet ? 'center' : 'left' }}
          >
            {descriptionText}
          </Text>
        </div>
        <ShortText>
          <Text size={18} color={'blueGray'} fontWeight={600} style={{ display: 'block' }}>
            {credits > 0 ? `+${credits}\xa0h` : `${credits}\xa0h`}
          </Text>
        </ShortText>
      </BookingHistoryItemWrapper>
    </BookingHistoryItemCard>
  );
};

type PaymentHistoryItemProps = {
  booking?: Booking;
  change?: number;
  type?: any;
  flags?: TutorCreditEventFlags;
  creditEvent: TutorCreditEvent;
};
export const PaymentHistoryItem: FC<PaymentHistoryItemProps> = ({
  booking,
  change,
  type,
  flags,
}) => {
  const subjects = useSelector(getSubjectsByNumber) || {};
  const _isTrialBooking = isTrialBooking(booking);
  const seeker = booking?.seeker;
  const { maxTablet } = useBreakpoints();

  const isDeletedUser = () => !seeker?.firstName;
  const yourSeeker = isDeletedUser()
    ? translate(translationKeys.offerRequestMessages.anonymousUser)
    : seeker?.firstName + ' ' + seeker?.lastName;

  const hint =
    type === ElitetutorCreditEventType.penalty
      ? translate(translationKeys.earnings.eventText.penalty)
      : flags?.seekerNoShow && flags?.tutorNoShow
      ? translate(translationKeys.earnings.eventText.bothNoShow)
      : flags?.seekerNoShow && !flags?.tutorNoShow
      ? translate(translationKeys.earnings.eventText.seekerNoShow, {
          name: yourSeeker,
        })
      : '';

  return (
    <BookingHistoryItemCard>
      <Hint>
        <Text size={15} color={'blueGray'} fontWeight={600} style={{ display: 'block' }}>
          {hint}
        </Text>
      </Hint>
      <BookingHistoryItemWrapper maxTablet={maxTablet}>
        <HistoryItemDefaultPart
          label={translate(translationKeys.billing.bookingHistory.yourSeeker)}
          value={yourSeeker}
        />
        <HistoryItemDefaultPart
          label={translate(translationKeys.billing.bookingHistory.time)}
          value={moment(booking?.start).format('HH:mm')}
        />
        <HistoryItemDefaultPart
          label={translate(translationKeys.billing.bookingHistory.date)}
          value={moment(booking?.start).format('DD.MM.YYYY')}
        />
        <HistoryItemDefaultPart
          label={translate(translationKeys.billing.bookingHistory.lessonType)}
          value={_isTrialBooking ? 'Probestunde' : 'Regulär'}
        />
        <HistoryItemDefaultPart
          label={translate(translationKeys.billing.bookingHistory.subject)}
          value={subjects[booking?.details?.subject || 0]?.subjectName || 'UNKNOWN'}
        />
        <div style={{ gridRow: 'span 2 / auto' }}>
          <Text size={20} color={'blueGray'} fontWeight={600} style={{ display: 'block' }}>
            {generatePriceFromChangeProp(change)}
          </Text>
        </div>
      </BookingHistoryItemWrapper>
    </BookingHistoryItemCard>
  );
};

export const PaymentHistoryItemWithText: FC<PaymentHistoryItemProps> = ({
  booking,
  change,
  type,
  creditEvent: { userNote, receiptUrl },
}) => {
  const { maxMobileLarge, maxTablet } = useBreakpoints();

  const emoji =
    type === ElitetutorCreditEventType.bonus_trial
      ? '🥳'
      : type === ElitetutorCreditEventType.payout
      ? '💸'
      : type === ElitetutorCreditEventType.correction
      ? '⚙️'
      : '';
  const isDeletedSeeker = !booking?.seeker?.firstName;
  const descriptionText =
    type === ElitetutorCreditEventType.bonus_trial
      ? translate(translationKeys.earnings.eventText.bonusTrial, {
          name: isDeletedSeeker
            ? translate(translationKeys.offerRequestMessages.anonymousUser)
            : booking?.seeker?.firstName + ' ' + booking?.seeker?.lastName,
        })
      : type === ElitetutorCreditEventType.payout
      ? translate(translationKeys.earnings.eventText.payout, {
          amount: generatePriceFromChangeProp(change).replace('-', ''),
        })
      : type === ElitetutorCreditEventType.correction
      ? translate(translationKeys.earnings.eventText.correction, {
          amount: generatePriceFromChangeProp(change),
        }) +
        (userNote
          ? ' ' +
            translate(translationKeys.billing.bookingHistory.correctionNoteByAdmin, {
              note: userNote,
            })
          : '')
      : '';

  return (
    <BookingHistoryItemCard>
      <BookingHistoryItemWrapper maxTablet={maxTablet}>
        {!maxMobileLarge && (
          <ShortText>
            <EmojiSpan role={'img'} aria-label={'party-smiley'}>
              {emoji}
            </EmojiSpan>
          </ShortText>
        )}
        <div style={{ gridColumn: 'span 5 / auto', textAlign: 'left', flex: 1 }}>
          <Text
            size={16}
            color={'blueGray'}
            fontWeight={600}
            style={{ display: 'block', textAlign: maxTablet ? 'center' : 'left' }}
          >
            {descriptionText}
          </Text>
          {receiptUrl && (
            <a href={receiptUrl} target={'_blank'} rel={'noreferrer'}>
              {translate(translationKeys.earnings.eventText.receipt)}
            </a>
          )}
        </div>
        <ShortText>
          <Text size={20} color={'blueGray'} fontWeight={600} style={{ display: 'block' }}>
            {generatePriceFromChangeProp(change)}
          </Text>
        </ShortText>
      </BookingHistoryItemWrapper>
    </BookingHistoryItemCard>
  );
};
